/**
 * Venue address search input for the public inquiry form.
 *
 * Same behaviour as the admin AddressAutocomplete (warehouse address, #23):
 * queries the public Places Autocomplete proxy as you type and shows a
 * dropdown of suggestions. Selecting one fills the field; free text is still
 * allowed, so it degrades to a plain input when address search is
 * unconfigured (empty suggestion list).
 *
 * Follows DESIGN.md: option rows are raw buttons with cursor-pointer, the
 * dropdown sits on a border (no shadow).
 */
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@shared/api-client";
import styles from "./AddressAutocomplete.module.css";

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

async function autocompleteAddress(
  query: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const res = await apiFetch<{ suggestions: { description: string }[] }>(
    "/api/places/autocomplete",
    { query: { q: query }, signal },
  );
  return (res.suggestions ?? []).map((s) => s.description);
}

/** Coordinates for a picked suggestion, resolved server-side. */
export interface PickedAddress {
  address: string;
  lat: number;
  lng: number;
}

/**
 * Geocode the exact suggestion the customer picked, so the inquiry carries
 * verified coordinates instead of free text that gets re-geocoded later.
 * Failure returns null — coords are an enrichment, never a blocker.
 */
export async function geocodePickedAddress(
  address: string,
): Promise<PickedAddress | null> {
  try {
    const res = await apiFetch<{ lat: number; lng: number }>(
      "/api/places/geocode",
      { query: { q: address } },
    );
    if (typeof res.lat !== "number" || typeof res.lng !== "number") return null;
    return { address, lat: res.lat, lng: res.lng };
  } catch {
    return null;
  }
}

export function AddressAutocomplete({
  id,
  value,
  onChange,
  onPick,
  ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  /** Fired when a suggestion is selected (after onChange), letting the
   *  caller geocode the picked address. Plain typing never fires it. */
  onPick?: (picked: string) => void;
  ariaLabel?: string;
}) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  // Suppress the fetch triggered by programmatically setting the field after
  // a pick — otherwise selecting a suggestion immediately re-opens the list.
  const justPicked = useRef(false);
  const debounced = useDebouncedValue(value, 250);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (justPicked.current) {
      justPicked.current = false;
      return;
    }
    if (!debounced || debounced.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const ac = new AbortController();
    autocompleteAddress(debounced, ac.signal)
      .then((res) => {
        setSuggestions(res);
        setOpen(true);
      })
      .catch(() => setSuggestions([]));
    return () => ac.abort();
  }, [debounced]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // A suggestion identical to the current value is noise — the field
  // already says it (typing the full address, or a re-focus after a pick).
  const visibleSuggestions = suggestions.filter((s) => s !== value);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <input
        id={id}
        className={styles.input}
        value={value}
        aria-label={ariaLabel}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => visibleSuggestions.length > 0 && setOpen(true)}
      />
      {open && visibleSuggestions.length > 0 && (
        <div className={styles.suggestions}>
          {visibleSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              className={styles.option}
              onClick={() => {
                justPicked.current = true;
                onChange(s);
                onPick?.(s);
                setSuggestions([]);
                setOpen(false);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
